import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as OpenAPISampler from "openapi-sampler";

import {
  Form,
  FormSingleFieldRow,
  FormInput,
  FormSubmit,
  FormSelect,
  FormTextArea,
  FormToggle,
} from "components/Form";

export interface DynamicFormData {
  [fieldName: string]: string | number | object;
}
export interface AsyncApiParameterSchemas {
  [parameterName: string]: AsyncApiParameterSchema;
}

export type AsyncApiParameterSchema =
  | {
      type: "string";
      description?: string;
      enum?: string[];
      title?: string;
      default?: string;
    }
  | {
      type: "number" | "integer";
      description?: string;
      minimum?: number;
      maximum?: number;
      title?: string;
      default?: number;
    }
  | {
      type: "array";
      description?: string;
      items: unknown;
      title?: string;
      default?: number;
    }
  | {
      type: "boolean";
      default: boolean;
      description?: string;
      title?: string;
    };

export interface DynamicFormProps {
  onSubmit: (data: DynamicFormData) => void | Promise<void>;
  isLoading: boolean;
  formSchema: AsyncApiParameterSchemas;
  submitButtonText: string;
}
const isSupported = (paramSchema: AsyncApiParameterSchema) =>
  paramSchema.type === "string" ||
  paramSchema.type === "number" ||
  paramSchema.type === "integer" ||
  paramSchema.type === "boolean";

const convertValue = (paramSchema: AsyncApiParameterSchema, value: string) => {
  if (paramSchema.type === "string") return value;
  if (paramSchema.type === "boolean") return value;
  if (paramSchema.type === "number" || paramSchema.type === "integer")
    return parseFloat(value);
  if (!isSupported(paramSchema)) return JSON.parse(value);
};

const DynamicFormField = ({
  name,
  value,
  register,
  errors,
}: {
  name: string;
  value: AsyncApiParameterSchema;
  register: ReturnType<typeof useForm>["register"];
  errors: ReturnType<typeof useForm>["errors"];
}) => {
  const [checked, setChecked] = useState(
    value.type === "boolean" ? value.default : false
  );

  if (!isSupported(value))
    return (
      <>
        <FormTextArea
          name={name}
          label={value.title || name}
          note={value.description}
          placeholder=""
          register={register}
          errors={errors}
          required
          validate={{
            isValidJson: (data) => {
              try {
                JSON.parse(data);
                return true;
              } catch {
                return false;
              }
            },
          }}
        />
        {errors[name] && errors[name]?.type === "isValidJson" && (
          <p className="text-red-500 text-xs italic">{name} has invalid json</p>
        )}
      </>
    );
  if (value.type === "boolean") {
    return (
      <>
        <FormToggle
          name={name}
          label={value.title || name}
          note={value.description}
          checked={checked}
          setChecked={setChecked}
          placeholder=""
          register={register}
          errors={errors}
          required
          defaultValue={value.default}
        />
      </>
    );
  }
  if (value.type === "string" && value.enum)
    return (
      <FormSelect
        name={name}
        label={value.title || name}
        options={value.enum.map((x) => ({ text: x, value: x }))}
        note={value.description}
        placeholder=""
        register={register}
        errors={errors}
        required
      />
    );
  const isNumeric = value.type === "number" || value.type === "integer";
  return (
    <FormInput
      type={isNumeric ? "number" : "text"}
      name={name}
      label={value.title || name}
      min={
        value.type === "number" || value.type === "integer"
          ? value.minimum
          : undefined
      }
      max={
        value.type === "number" || value.type === "integer"
          ? value.maximum
          : undefined
      }
      note={value.description}
      placeholder=""
      defaultValue={value.default === undefined ? "" : value.default.toString()}
      register={register}
      errors={errors}
      required
    />
  );
};

export const DynamicForm = ({
  onSubmit,
  isLoading,
  formSchema,
  submitButtonText,
}: DynamicFormProps) => {
  const {
    register,
    handleSubmit,
    errors,
    reset,
    formState: { isSubmitting },
  } = useForm<Record<string, string>>();
  register();
  // this seems to need a body scoped here, rather than just delegating
  // direct to onSubmit for tests to work
  const submitCallback = handleSubmit((args) => {
    return onSubmit(
      Object.fromEntries(
        Object.entries(args).map(([key, value]) => [
          key,
          convertValue(formSchema[key], value),
        ])
      )
    );
  });

  useMemo(() => {
    const templateJsonForUnsupportedFields = Object.fromEntries(
      Object.entries(formSchema)
        .filter(([key, value]) => !isSupported(value))
        .map(([name, value]) => [
          name,
          JSON.stringify(OpenAPISampler.sample(value)),
        ]) || []
    );
    reset(templateJsonForUnsupportedFields);
  }, [formSchema, reset]);

  return (
    <Form onSubmit={submitCallback}>
      {Object.entries(formSchema).map(([name, value], index) => (
        <FormSingleFieldRow key={index}>
          <DynamicFormField
            name={name}
            value={value}
            register={register}
            errors={errors}
          />
        </FormSingleFieldRow>
      ))}
      <FormSubmit
        isLoading={isSubmitting || isLoading || !formSchema}
        label={submitButtonText}
      />
    </Form>
  );
};
